import React, { useState, useRef, useEffect } from 'react';

/**
 * ExportMenu — inline-styles only (no Tailwind/Bootstrap)
 * Props:
 *   label   — button label text (default "Export")
 *   onExcel — async fn for Excel export
 *   onPdf   — async fn for PDF export
 *   onWord  — async fn for Word export
 *   size    — "sm" | "md" (default "md")
 */
export default function ExportMenu({ label = 'Export', onExcel, onPdf, onWord, size = 'md' }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClick = async (format, fn) => {
    if (!fn || loading) return;
    setOpen(false);
    setLoading(format);
    try { await fn(); }
    catch (err) { console.error('Export failed:', err); }
    finally { setLoading(null); }
  };

  const isSm = size === 'sm';
  const btnSize = isSm
    ? { padding: '4px 10px', fontSize: 12, gap: 4 }
    : { padding: '6px 14px', fontSize: 13, gap: 6 };

  const btnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    borderRadius: 8,
    fontWeight: 600,
    border: '1px solid',
    whiteSpace: 'nowrap',
    background: open || loading ? '#4680ff' : '#ffffff',
    color: open || loading ? '#ffffff' : '#475569',
    borderColor: open || loading ? '#3569e0' : '#cbd5e1',
    ...btnSize,
  };

  const options = [
    { format: 'excel', label: 'Excel (.xlsx)', icon: '📊', fn: onExcel },
    { format: 'pdf',   label: 'PDF (.pdf)',    icon: '📄', fn: onPdf   },
    { format: 'word',  label: 'Word (.docx)',  icon: '📝', fn: onWord  },
  ];

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={ref}>
      <button style={btnStyle} onClick={() => !loading && setOpen((v) => !v)} title="Export">
        {loading ? (
          <span style={{ opacity: 0.8 }}>Exporting…</span>
        ) : (
          <>
            {/* download icon */}
            <svg width={isSm ? 13 : 15} height={isSm ? 13 : 15} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            {label}
            {/* chevron */}
            <svg width={isSm ? 10 : 12} height={isSm ? 10 : 12} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)',
          width: 168, background: '#ffffff', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0',
          zIndex: 9999, overflow: 'hidden',
        }}>
          {options.map(({ format, label: optLabel, icon, fn }) => (
            <button
              key={format}
              onClick={() => handleClick(format, fn)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', textAlign: 'left',
                padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                background: 'none', border: 'none', color: '#334155',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#334155'; }}
            >
              <span style={{ fontSize: 15 }}>{icon}</span>
              {optLabel}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
